/**
 * Custom Field Store
 * Manages custom field state using Zustand
 */

import { create } from 'zustand';
import { CustomField, TaskCustomFieldValues } from '@/types';

interface CustomFieldState {
  // State
  customFields: CustomField[];
  taskValues: Map<string, TaskCustomFieldValues>;
  isLoading: boolean;
  error: string | null;

  // Actions
  setCustomFields: (fields: CustomField[]) => void;
  addCustomField: (field: CustomField) => void;
  updateCustomField: (field: CustomField) => void;
  removeCustomField: (customFieldId: string) => void;
  setTaskValues: (taskId: string, values: TaskCustomFieldValues) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearCustomFields: () => void;
}

export const useCustomFieldStore = create<CustomFieldState>()((set) => ({
  // Initial state
  customFields: [],
  taskValues: new Map(),
  isLoading: false,
  error: null,

  // Actions
  setCustomFields: (fields) => set({ customFields: fields }),

  addCustomField: (field) =>
    set((state) => ({
      customFields: [...state.customFields, field],
    })),

  updateCustomField: (field) =>
    set((state) => ({
      customFields: state.customFields.map((f) =>
        f.customFieldId === field.customFieldId ? field : f
      ),
    })),

  removeCustomField: (customFieldId) =>
    set((state) => ({
      customFields: state.customFields.filter(
        (f) => f.customFieldId !== customFieldId
      ),
    })),

  setTaskValues: (taskId, values) =>
    set((state) => {
      const newTaskValues = new Map(state.taskValues);
      newTaskValues.set(taskId, values);
      return { taskValues: newTaskValues };
    }),

  setIsLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  clearCustomFields: () =>
    set({
      customFields: [],
      taskValues: new Map(),
      error: null,
    }),
}));

// Selectors
export const useCustomFields = () =>
  useCustomFieldStore((state) => state.customFields);

export const useActiveCustomFields = () =>
  useCustomFieldStore((state) => state.customFields.filter((f) => f.isActive));

export const useTaskCustomFieldValues = (taskId: string) =>
  useCustomFieldStore((state) => state.taskValues.get(taskId));

export const useCustomFieldLoading = () =>
  useCustomFieldStore((state) => state.isLoading);

export const useCustomFieldError = () =>
  useCustomFieldStore((state) => state.error);

export const useCustomFieldById = (customFieldId: string) =>
  useCustomFieldStore((state) =>
    state.customFields.find((f) => f.customFieldId === customFieldId)
  );

export const useRequiredCustomFields = () =>
  useCustomFieldStore((state) =>
    state.customFields.filter((f) => f.isRequired && f.isActive)
  );

export default useCustomFieldStore;
